import { useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';

import { Api } from '../api/api';
import type { Child } from '../api/models';
import { AnchoredBack } from '../components/AnchoredBack';
import { AnchoredPrompt } from '../components/AnchoredPrompt';
import { SecondaryButton } from '../components/SecondaryButton';
import { AchievementsScreen } from '../screens/Achievements/AchievementsScreen';
import { AddChildScreen } from '../screens/AddChild/AddChildScreen';
import { ChildrenScreen } from '../screens/Children/ChildrenScreen';
import { DashboardScreen } from '../screens/Dashboard/DashboardScreen';
import { OverviewScreen } from '../screens/Overview/OverviewScreen';
import { SettingsScreen } from '../screens/Settings/SettingsScreen';
import { SkillDetailScreen } from '../screens/SkillDetail/SkillDetailScreen';
import { Strings } from '../strings';

import { ParentRoute } from './routes';

/**
 * design-system/06-Components/06-Navigation-And-Structure.md §1 — bottom navigation, three items.
 * Mirrors android/…/Navigation/ParentShell.kt.
 *
 * Denser than the child shell, and the only shell with tabs. Nothing here can reach a child screen: the
 * two shells are exclusive states of the client, not branches of one tree (ADR-0006).
 *
 * The bar sits at the bottom below 700px, where a thumb is, and moves to the top on a wider screen, where
 * a pointer is and where a bar pinned to the bottom of a desktop window reads as a system tray. Same three
 * items, same order, same labels.
 */
export function ParentShell({
  email,
  emailVerified,
  onEnteredChildMode,
  onSignOut,
}: {
  email: string;
  /** Nothing is withheld while this is false — the banner is the whole enforcement (R0066 D-1). */
  emailVerified: boolean;
  onEnteredChildMode: (child: Child) => void;
  onSignOut: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [notice, setNotice] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const backTarget = backTargetFor(location.pathname);

  const resend = async (): Promise<void> => {
    const result = await Api.resendVerification();

    setNotice(result.ok ? Strings.verifyEmailSent : result.error.message);
  };

  // What is floating over the bottom of the page right now. Both are `position: fixed`, so neither takes
  // room in the flow and the content has to leave it for them.
  const anchoredCount: number = (backTarget !== null ? 1 : 0) + (!emailVerified && !dismissed ? 1 : 0);

  return (
    <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      <main
        style={{
          flex: 1,
          width: '100%',
          maxWidth: 'var(--column)',
          margin: '0 auto',
          padding: 'var(--space-5)',
          /*
            Room for whatever is floating over the bottom of this screen, so its last row can be scrolled
            clear. Counted rather than assumed: the two anchored elements appear independently, and a
            screen showing both needs twice the room (R0146).
          */
          paddingBottom: `calc(var(--space-5) + var(--anchored-clearance) * ${anchoredCount})`,
        }}
      >
        {/*
          The banner, and nothing else. An unverified account is deliberately not blocked from anything:
          no address can be changed once verified, so whatever were withheld would be withheld permanently
          from a parent who mistyped theirs (R0066 D-1). It is `info`, never a warning — nothing is wrong.
        */}

        <Routes>
          <Route
            path="children"
            element={
              <ChildrenScreen
                onEnteredChildMode={onEnteredChildMode}
                onAddChild={() => navigate(ParentRoute.addChild)}
                onOpenDashboard={(childId: string) => navigate(ParentRoute.dashboard(childId))}
              />
            }
          />
          <Route
            path="overview"
            element={
              <OverviewScreen
                onOpenDashboard={(childId: string) => navigate(ParentRoute.dashboard(childId))}
              />
            }
          />
          <Route
            path="children/new"
            element={<AddChildScreen onCreated={() => navigate(ParentRoute.children)} />}
          />
          <Route path="children/:childId" element={<DashboardRoute />} />
          <Route path="children/:childId/achievements" element={<ParentAchievementsRoute />} />
          <Route path="children/:childId/:skillCode" element={<SkillRoute />} />
          <Route path="settings" element={<SettingsScreen email={email} onSignOut={onSignOut} />} />
          <Route path="*" element={<Navigate to={ParentRoute.children} replace />} />
        </Routes>
      </main>

      {/*
        One way back, and **the shell decides**, not the screen — design-system 06-Navigation-And-Structure
        §4a (R0124). A screen that owned its own back control is a screen that could render it in a fourth
        place, or forget it: Ocenenia had a „Späť" button at the top, the skill screen a „Prehľad" text
        link, and the child dashboard nothing at all.

        Reading it from the address rather than from a prop, because the address is what says where you
        came from.
      */}
      {backTarget !== null ? <AnchoredBack onBack={() => navigate(backTarget)} /> : null}

      {/*
        Anchored above the navigation rather than in the flow — design-system §4. Inline it pushed the
        children list down the page every time an unverified parent opened the app (R0099).
      */}
      {!emailVerified && !dismissed ? (
        <AnchoredPrompt text={notice ?? Strings.verifyEmailBanner}>
          {notice === null ? (
            <>
              <SecondaryButton
                label={Strings.verifyEmailResend}
                onClick={() => void resend()}
                fullWidth={false}
              />
              <SecondaryButton
                label={Strings.verifyEmailFixAddress}
                onClick={() => navigate(ParentRoute.settings)}
                fullWidth={false}
              />
            </>
          ) : (
            <SecondaryButton
              label={Strings.actionContinue}
              onClick={() => setDismissed(true)}
              fullWidth={false}
            />
          )}
        </AnchoredPrompt>
      ) : null}

      <TabBar />
    </div>
  );
}

function DashboardRoute() {
  const { childId } = useParams();
  const navigate = useNavigate();

  return (
    <DashboardScreen
      childId={childId ?? null}
      onOpenSkill={(skillCode: string) => navigate(ParentRoute.skill(childId ?? '', skillCode))}
      onOpenAchievements={() => navigate(ParentRoute.achievements(childId ?? ''))}
    />
  );
}

function ParentAchievementsRoute() {
  const { childId } = useParams();

  return <AchievementsScreen childId={childId ?? null} />;
}

function SkillRoute() {
  const { childId, skillCode } = useParams();

  return <SkillDetailScreen childId={childId ?? null} skillCode={skillCode ?? null} />;
}

const TABS = [
  { path: ParentRoute.children, label: Strings.navChildren },
  { path: ParentRoute.overview, label: Strings.navOverview },
  { path: ParentRoute.settings, label: Strings.navSettings },
];

function TabBar() {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <nav
      style={{
        position: 'sticky',
        bottom: 0,
        background: 'var(--surface-raised)',
        borderTop: '1px solid var(--border-default)',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div style={{ display: 'flex', width: '100%', maxWidth: 'var(--column)' }}>
        {TABS.map((tab) => {
          const isCurrent = location.pathname.startsWith(tab.path);

          return (
            <button
              key={tab.path}
              type="button"
              aria-current={isCurrent ? 'page' : undefined}
              onClick={() => navigate(tab.path)}
              style={{
                flex: 1,
                minHeight: 'calc(var(--touch-parent) + var(--space-4))',
                background: 'none',
                border: 'none',
                color: isCurrent ? 'var(--action-primary)' : 'var(--text-secondary)',
                fontSize: '15px',
                lineHeight: '20px',
                fontWeight: 600,
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

/**
 * Where „Späť" goes from an address, or `null` on a screen that was not navigated into.
 *
 * The three bottom-navigation destinations have no way back because there is nowhere to go back **to** —
 * they are where you start. Everything else is reached from something, and 06-Navigation-And-Structure.md
 * §4a says every one of those offers exactly one way out, in the same place (R0124).
 *
 * Derived from the address rather than passed down, so a screen cannot render a second back control of its
 * own or ship without one. That is what happened three times over: a „Späť" button at the top of Ocenenia,
 * a „Prehľad" text link on the skill screen, and nothing at all on the child dashboard.
 */
function backTargetFor(pathname: string): string | null {
  const parts = pathname
    .replace(/^\/parent\/?/, '')
    .split('/')
    .filter(Boolean);

  // children · overview · settings — the destinations themselves.
  if (parts.length <= 1) return null;

  // children/new — reached from the list, and goes back to it.
  if (parts[0] === 'children' && parts[1] === 'new') return ParentRoute.children;

  // children/:childId — the dashboard, reached from the list.
  if (parts.length === 2) return ParentRoute.children;

  // children/:childId/achievements and children/:childId/:skillCode — reached from that dashboard.
  return ParentRoute.dashboard(parts[1]);
}
